import React, { useCallback } from 'react'
import {
  FileTextOutlined,
  ClockCircleOutlined,
  DashboardOutlined,
  CheckCircleOutlined,
  UserOutlined,
  TeamOutlined,
  OrderedListOutlined,
  FlagOutlined,
  BulbOutlined,
  AimOutlined
} from '@ant-design/icons'
import { getBridgeWorkoutSets } from '../../../api/bridge'
import { useBridgeList } from './useBridgeList'
import { formatDate, formatDateTime } from './dateRange'
import BridgeTable from './bridgeTable'
import styles from './bridge.module.css'

const statusPill = (status) => {
  if (!status) return null
  const cls =
    status === 'completed'
      ? `${styles.pill} ${styles.pillGreen}`
      : `${styles.pill} ${styles.pillOrange}`
  const label =
    status === 'completed' ? 'On target' : status.charAt(0).toUpperCase() + status.slice(1)
  return <span className={cls}>{label}</span>
}

const weightReps = (weight, reps, unit) => {
  const parts = []
  if (weight != null) parts.push(`${weight}${unit || ''}`)
  if (reps != null) parts.push(`${reps} reps`)
  return parts.length ? parts.join(' × ') : null
}

const formatTime = (ms) => {
  if (ms == null) return null
  const totalSeconds = Math.round(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`
}

const formatTempo = (tempo) => {
  if (!tempo || !Object.keys(tempo).length) return null
  try {
    return JSON.stringify(tempo)
  } catch (err) {
    return null
  }
}

const formatMeasure = (record, prefix) => {
  const unit = record.weight_unit
  const parts = [
    weightReps(record[`${prefix}_weight`], record[`${prefix}_reps`], unit),
    formatTime(record[`${prefix}_time_ms`]),
    formatTempo(record[`${prefix}_tempo`])
  ].filter((part) => part != null)
  return parts.length ? parts.join(' · ') : null
}

export default function WorkoutSetsTab({ personId, dateRange }) {
  const fetcher = useCallback(
    (skip, limit) =>
      getBridgeWorkoutSets({
        personId,
        startDate: formatDate(dateRange[0]),
        endDate: formatDate(dateRange[1]),
        skip,
        limit
      }),
    [personId, dateRange]
  )
  const { data, count, loading, page, setPage, error } = useBridgeList(
    fetcher,
    [personId, dateRange]
  )

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date_time',
      width: 120,
      render: (v) => (v ? formatDateTime(v).split(' ')[0] : '—')
    },
    { title: 'Program', dataIndex: 'program_name', render: (v) => v || '—' },
    { title: 'Phase', dataIndex: 'phase_name', render: (v) => v || '—' },
    { title: 'Workout', dataIndex: 'workout_name', render: (v) => v || '—' },
    { title: 'Exercise', dataIndex: 'exercise_name', render: (v) => v || '—' },
    { title: 'Set', dataIndex: 'sequence', width: 60 },
    {
      title: 'Type',
      dataIndex: 'set_type',
      width: 90,
      render: (v) => v || '—'
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => statusPill(record.status) || '—'
    }
  ]

  return (
    <div className={styles.tableWrap}>
      <BridgeTable
        columns={columns}
        dataSource={data}
        loading={loading}
        count={count}
        page={page}
        onPageChange={(p) => setPage(p)}
        emptyText={error ? `Failed to load: ${error}` : 'No workout sets'}
        renderExpanded={(record) => {
          const person =
            record.person &&
            (record.person.full_name ||
              `${record.person.first_name || ''} ${record.person.last_name || ''}`.trim())
          const flags =
            record.has_flags && Object.keys(record.has_flags).length
              ? Object.entries(record.has_flags)
                  .map(([key, value]) => `${key}: ${value}`)
                  .join(', ')
              : null
          const prescribedWeight =
            record.prescribed_weight != null
              ? `${record.prescribed_weight}${record.weight_unit || ''}`
              : null
          const resultWeight =
            record.result_weight != null
              ? `${record.result_weight}${record.weight_unit || ''}`
              : null
          return (
            <div className={styles.expand}>
              <div className={styles.expandItem}>
                <BulbOutlined />
                <span className={styles.expandLabel}>Rx Weight</span>
                {prescribedWeight || '—'}
              </div>
              <div className={styles.expandItem}>
                <AimOutlined />
                <span className={styles.expandLabel}>Result Weight</span>
                {resultWeight || '—'}
              </div>
              <div className={styles.expandItem}>
                <BulbOutlined />
                <span className={styles.expandLabel}>Rx Reps</span>
                {record.prescribed_reps == null ? '—' : `${record.prescribed_reps} reps`}
              </div>
              <div className={styles.expandItem}>
                <AimOutlined />
                <span className={styles.expandLabel}>Result Reps</span>
                {record.result_reps == null ? '—' : `${record.result_reps} reps`}
              </div>
              <div className={styles.expandItem}>
                <ClockCircleOutlined />
                <span className={styles.expandLabel}>Rx Time</span>
                {formatTime(record.prescribed_time_ms) || '—'}
              </div>
              <div className={styles.expandItem}>
                <ClockCircleOutlined />
                <span className={styles.expandLabel}>Result Time</span>
                {formatTime(record.result_time_ms) || '—'}
              </div>
              <div className={styles.expandItem}>
                <DashboardOutlined />
                <span className={styles.expandLabel}>Rx Tempo</span>
                {formatTempo(record.prescribed_tempo) || '—'}
              </div>
              <div className={styles.expandItem}>
                <DashboardOutlined />
                <span className={styles.expandLabel}>Result Tempo</span>
                {formatTempo(record.result_tempo) || '—'}
              </div>
              <div className={styles.expandItem}>
                <CheckCircleOutlined />
                <span className={styles.expandLabel}>Status</span>
                {statusPill(record.status) || '—'}
              </div>
              <div className={styles.expandItem}>
                <FileTextOutlined />
                <span className={styles.expandLabel}>Note</span>
                {record.note || '—'}
              </div>
              <div className={styles.expandItem}>
                <ClockCircleOutlined />
                <span className={styles.expandLabel}>Rest</span>
                {record.rest_time != null ? `${record.rest_time}s` : '—'}
              </div>
              <div className={styles.expandItem}>
                <DashboardOutlined />
                <span className={styles.expandLabel}>Tempo</span>
                {record.result_tempo && Object.keys(record.result_tempo).length
                  ? JSON.stringify(record.result_tempo)
                  : '—'}
              </div>
              <div className={styles.expandItem}>
                <CheckCircleOutlined />
                <span className={styles.expandLabel}>Status</span>
                {statusPill(record.status) || '—'}
              </div>
              <div className={styles.expandItem}>
                <UserOutlined />
                <span className={styles.expandLabel}>Person</span>
                {person || '—'}
              </div>
              <div className={styles.expandItem}>
                <TeamOutlined />
                <span className={styles.expandLabel}>Team</span>
                {record.bridge_team_id || '—'}
              </div>
              <div className={styles.expandItem}>
                <OrderedListOutlined />
                <span className={styles.expandLabel}>Round</span>
                {record.round_number == null ? '—' : record.round_number}
              </div>
              <div className={styles.expandItem}>
                <FlagOutlined />
                <span className={styles.expandLabel}>Flags</span>
                {flags || '—'}
              </div>
            </div>
          )
        }}
      />
    </div>
  )
}
